import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/api';
import StatCard from '../shared/StatCard';
import './KpiRow.css';

interface StatsShape {
  execution_summary: { success_rate: number; total_executed: number };
  coverage: { percentage: number };
  critical_failures: number;
  comparisons: {
    success_rate_delta: number | null;
    coverage_delta: number | null;
  };
}

interface Props {
  stats: StatsShape | null;
  loading: boolean;
  days: number;
}

const CheckIcon = <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>;
const TargetIcon = <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>;
const AlertIcon = <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>;
const SparkleIcon = <path d="M12 2l1.9 5.8L20 10l-6.1 2.2L12 18l-1.9-5.8L4 10l6.1-2.2L12 2z"/>;

export const KpiRow: React.FC<Props> = ({ stats, loading, days }) => {
  const [aiLoading, setAiLoading] = useState(true);
  const [aiHours, setAiHours] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAiLoading(true);
    apiClient.get(`/api/v1/dashboard/ai-productivity?days=${days}`)
      .then(resp => { if (!cancelled) setAiHours((resp.data as any).estimated_hours_saved); })
      .catch(() => { if (!cancelled) setAiHours(null); })
      .finally(() => { if (!cancelled) setAiLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const rateDelta = stats?.comparisons.success_rate_delta;
  const covDelta = stats?.comparisons.coverage_delta;

  return (
    <div className="kpi-row-shared">
      <StatCard
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{CheckIcon}</svg>}
        iconColor="green"
        label="Test Pass Rate"
        value={loading ? '—' : `${stats?.execution_summary.success_rate ?? 0}%`}
        tooltip="% of tests that passed out of everything executed in the selected period. Compared to the equal-length period before it."
        trend={rateDelta != null ? { value: Math.abs(rateDelta), direction: rateDelta === 0 ? 'flat' : rateDelta > 0 ? 'up' : 'down', suffix: 'pt' } : undefined}
      />
      <StatCard
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{TargetIcon}</svg>}
        iconColor="blue"
        label="Test Coverage"
        value={loading ? '—' : `${stats?.coverage.percentage ?? 0}%`}
        sublabel="Scenarios executed ≥ 1 time"
        tooltip="% of your test scenarios that have been run at least once, ever — not limited to the selected period. Low coverage means many scenarios have never been executed."
        trend={covDelta != null ? { value: Math.abs(covDelta), direction: covDelta === 0 ? 'flat' : covDelta > 0 ? 'up' : 'down', suffix: 'pt' } : undefined}
      />
      <StatCard
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{AlertIcon}</svg>}
        iconColor={stats && stats.critical_failures > 0 ? 'red' : 'green'}
        label="Critical Failures"
        value={loading ? '—' : stats?.critical_failures ?? 0}
        sublabel={stats && stats.critical_failures > 0 ? 'Needs investigation' : 'None this period'}
        tooltip="Number of Critical-priority scenarios/test cases that FAILED in the selected period. This ignores Medium/Low priority failures — it's specifically the failures most likely to block a release."
      />
      <StatCard
        icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{SparkleIcon}</svg>}
        iconColor="purple"
        label="AI Time Saved"
        value={aiLoading ? '—' : `${aiHours ?? 0} hrs`}
        sublabel="Estimated"
        tooltip="Estimated QA hours saved this period by generating scenarios/test cases with AI instead of writing them by hand. This is an estimate, not a measurement — see AI Productivity below for the formula."
      />
    </div>
  );
};

export default KpiRow;
